import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Spin,
  message,
  Table,
  Tag,
  Modal,
  Alert,
  Typography,
  Tooltip,
  Empty,
  Descriptions,
  Result
} from 'antd';
import {
  CreditCardOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  StarOutlined,
  StarFilled,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/operator/governanceService';
import ListPageHeader from '@shared/ListPageHeader';

const { Text } = Typography;

// Outcome dispatch table for 3D verification return.
// Keys must match values produced by PayuniController#compute_binding_outcome.
const BINDING_OUTCOMES = {
  success: { severity: 'success', titleKey: '3d_verification_complete' },
  duplicate: { severity: 'info', titleKey: 'outcomes.duplicate.title', messageKey: 'outcomes.duplicate.message' },
  verification_failed: { severity: 'error', titleKey: 'add_card_failed', messageKey: 'verification_failed_desc' },
  failed: { severity: 'error', titleKey: 'add_card_failed', messageKey: 'card_binding_failed' }
};

const CARD_BRAND_COLORS = {
  visa: '#1a1f71',
  mastercard: '#eb001b',
  jcb: '#0066cc',
  amex: '#006fcf',
  unknown: '#666666'
};

const PaymentMethods = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bankTransferInfo, setBankTransferInfo] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const [isEnterprise, setIsEnterprise] = useState(false);

  // Card binding modal state
  const [addCardModalVisible, setAddCardModalVisible] = useState(false);
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [bindingStatus, setBindingStatus] = useState(null); // null, 'processing', 'success', 'error'
  const [bindingError, setBindingError] = useState(null);
  const [currentSdkToken, setCurrentSdkToken] = useState(null); // Store SDK token for bind_card API
  const payuniContainerRef = useRef(null);
  const uniPaymentRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [response, subResponse] = await Promise.all([
        governanceService.getPaymentMethods(),
        governanceService.getSubscription().catch(() => null)
      ]);

      if (subResponse?.success) {
        setIsEnterprise(subResponse.data.subscription?.pricing_plan?.plan_type === 'enterprise');
      }

      if (response.success) {
        setPaymentMethods(response.data.payment_methods || []);
        setBankTransferInfo(response.data.bank_transfer_info);
      } else {
        message.error(response.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle return from 3D Secure verification.
  // Backend is source of truth for the outcome class; this component only
  // dispatches presentation. To add a new outcome: extend BINDING_OUTCOMES
  // and add the i18n keys — no branching logic to touch.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('binding_complete') !== 'true') return;

    const outcome = urlParams.get('outcome') || 'failed';
    console.log('[PayUNi] Returned from 3D verification, outcome:', outcome);

    sessionStorage.removeItem('payuni_binding_in_progress');
    window.history.replaceState({}, document.title, window.location.pathname);

    const meta = BINDING_OUTCOMES[outcome] || BINDING_OUTCOMES.failed;
    const ns = 'operator.governance.payment_methods.';
    const title = t(ns + meta.titleKey);
    const content = meta.messageKey ? t(ns + meta.messageKey) : undefined;

    if (meta.severity === 'success') {
      message.success(title);
    } else {
      Modal[meta.severity]({ title, content });
    }

    fetchData();
  }, [fetchData, t]);

  // Load PayUNi SDK script dynamically
  const loadPayuniSdk = useCallback((sdkUrl) => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.UNiPayment || window.UniPayment) {
        console.log('[PayUNi] SDK already loaded');
        resolve();
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector(`script[src="${sdkUrl}"]`);
      if (existingScript) {
        console.log('[PayUNi] Script tag exists, waiting for load');
        // Wait a bit for existing script to finish loading
        setTimeout(() => resolve(), 500);
        return;
      }

      const script = document.createElement('script');
      script.src = sdkUrl;
      script.async = true;
      script.onload = () => {
        console.log('[PayUNi] SDK script loaded');
        // Give SDK time to initialize
        setTimeout(() => {
          // Debug: Check what globals are available after SDK loads
          console.log('[PayUNi] Available globals after load:');
          console.log('  - UNiPayment:', typeof window.UNiPayment);
          console.log('  - UniPayment:', typeof window.UniPayment);
          console.log('  - UPi:', typeof window.UPi);
          console.log('  - PAYUNi:', typeof window.PAYUNi);
          resolve();
        }, 300);
      };
      script.onerror = (error) => {
        console.error('[PayUNi] SDK load failed:', error);
        reject(new Error('Failed to load PayUNi SDK'));
      };
      document.head.appendChild(script);
    });
  }, []);

  // Initialize PayUNi UNi Embed
  const initializePayuni = useCallback(async () => {
    setAddCardLoading(true);
    setBindingStatus(null);
    setBindingError(null);

    try {
      // 1. Get SDK token from backend
      const response = await governanceService.getSdkToken(
        window.location.href, // return_url
        1 // amount for preauth ($1)
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to get SDK token');
      }

      const { sdk_token, sdk_url, gateway, merchant_id } = response.data;
      console.log('[PayUNi] SDK token received:', { gateway, merchant_id, sdk_url });
      console.log('[PayUNi] Token (first 20 chars):', sdk_token?.substring(0, 20) + '...');

      // Store SDK token for later use in bind_card API
      setCurrentSdkToken(sdk_token);

      // 2. Load SDK script
      await loadPayuniSdk(sdk_url);
      setSdkLoaded(true);

      // 3. Wait for container to be ready
      await new Promise(resolve => setTimeout(resolve, 200));

      // 4. Check available PayUNi SDK globals
      console.log('[PayUNi] Checking SDK availability...');
      console.log('[PayUNi] Container ref:', payuniContainerRef.current);

      // Try different possible class/function names
      const PayUNiSDK = window.UNiPayment || window.UniPayment || window.UPi || window.PAYUNi;

      if (!PayUNiSDK) {
        // List all window properties that might be PayUNi related
        const possibleGlobals = Object.keys(window).filter(key =>
          key.toLowerCase().includes('pay') ||
          key.toLowerCase().includes('uni') ||
          key.toLowerCase().includes('upi')
        );
        console.log('[PayUNi] Possible payment-related globals:', possibleGlobals);

        throw new Error('PayUNi SDK not found. Please check SDK URL and ensure it loads correctly.');
      }

      console.log('[PayUNi] SDK found:', PayUNiSDK);
      console.log('[PayUNi] SDK type:', typeof PayUNiSDK);
      console.log('[PayUNi] SDK methods:', Object.keys(PayUNiSDK));

      // 5. Initialize based on SDK API
      // PayUNi SDK can have different API patterns:
      // - new UNiPayment({ token, container, ... })
      // - UNiPayment.createSession({ token, ... })
      // - UNiPayment.init({ token, ... })

      const containerId = 'payuni-container';

      if (typeof PayUNiSDK === 'function') {
        // Constructor pattern
        console.log('[PayUNi] Using constructor pattern');
        uniPaymentRef.current = new PayUNiSDK({
          token: sdk_token,
          container: containerId,
          onReady: () => {
            console.log('[PayUNi] Payment form ready');
            setAddCardLoading(false);
          },
          onSuccess: (result) => {
            console.log('[PayUNi] Card binding success:', result);
            setBindingStatus('success');
            setTimeout(() => {
              fetchData();
              setAddCardModalVisible(false);
              message.success(t('operator.governance.payment_methods.add_card_success'));
            }, 2000);
          },
          onError: (error) => {
            console.error('[PayUNi] Card binding error:', error);
            setBindingStatus('error');
            setBindingError(error.message || 'Card binding failed');
          },
          onCancel: () => {
            console.log('[PayUNi] Card binding cancelled');
            setAddCardModalVisible(false);
          }
        });
      } else if (typeof PayUNiSDK.createSession === 'function') {
        // PayUNi SDK createSession pattern
        // API: UniPayment.createSession(SDK_TOKEN, options)
        // Documentation: https://docs.payuni.com.tw/
        console.log('[PayUNi] Using createSession pattern');

        // Determine environment based on SDK URL
        const isSandbox = sdk_url?.includes('sandbox');

        // PayUNi SDK options - based on official documentation
        const sdkOptions = {
          env: isSandbox ? 'S' : 'P',  // S: 測試環境, P: 正式環境
          useInst: false,  // 是否使用分期
          elements: {
            CardNo: 'payuni-card-no',
            CardExp: 'payuni-card-exp',
            CardCvc: 'payuni-card-cvc',
            CardTokenType: 'payuni-token-type'  // For 約定信用卡
          },
          style: {
            color: '#000000',
            errorColor: '#FF0000',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '24px'
          }
        };

        console.log('[PayUNi] SDK options:', sdkOptions);
        console.log('[PayUNi] Token (first 20 chars):', sdk_token?.substring(0, 20) + '...');

        // Create session with (token, options) - two separate parameters
        uniPaymentRef.current = window.UniPayment.createSession(sdk_token, sdkOptions);
        console.log('[PayUNi] createSession result:', uniPaymentRef.current);

        // Set up onUpdate callback for form status
        uniPaymentRef.current.onUpdate((update) => {
          const { status, event, data } = update;
          console.log('[PayUNi] onUpdate:', { status, event, data });

          // Handle form validation status
          if (status) {
            const allValid = status.CardNo === true && status.CardExp === true && status.CardCvc === true;
            console.log('[PayUNi] Form valid:', allValid, status);
          }

          // Handle useTokenType event (記憶卡號/約定信用卡)
          if (event === 'useTokenType' && data) {
            console.log('[PayUNi] Token type event:', data);
            const tokenTypeArea = document.getElementById('payuni-token-type-area');
            if (tokenTypeArea) {
              tokenTypeArea.style.display = 'flex';
            }
            const tokenTypeText = document.getElementById('payuni-token-type-text');
            if (tokenTypeText && data.tokenTypeText) {
              tokenTypeText.innerHTML = data.tokenTypeText;
            }
          }
        });

        // Start the SDK - this validates origin/token and displays input fields
        try {
          const startResult = await uniPaymentRef.current.start();
          console.log('[PayUNi] start() success:', startResult);
          setAddCardLoading(false);
        } catch (startError) {
          console.error('[PayUNi] start() failed:', startError);
          throw new Error(startError.message || 'Failed to initialize payment form');
        }
      } else if (typeof PayUNiSDK.init === 'function') {
        // init pattern
        console.log('[PayUNi] Using init pattern');
        uniPaymentRef.current = PayUNiSDK.init({
          token: sdk_token,
          container: containerId,
          onReady: () => {
            console.log('[PayUNi] Payment form ready');
            setAddCardLoading(false);
          },
          onSuccess: (result) => {
            console.log('[PayUNi] Card binding success:', result);
            setBindingStatus('success');
            setTimeout(() => {
              fetchData();
              setAddCardModalVisible(false);
              message.success(t('operator.governance.payment_methods.add_card_success'));
            }, 2000);
          },
          onError: (error) => {
            console.error('[PayUNi] Card binding error:', error);
            setBindingStatus('error');
            setBindingError(error.message || 'Card binding failed');
          }
        });
      } else {
        console.error('[PayUNi] Unknown SDK API pattern. Available:', PayUNiSDK);
        throw new Error('Unknown PayUNi SDK API pattern');
      }

    } catch (error) {
      console.error('[PayUNi] Initialization error:', error);
      setAddCardLoading(false);
      setBindingStatus('error');
      setBindingError(error.message);
    }
  }, [fetchData, loadPayuniSdk, t]);

  // Handle add card button click
  const handleAddCard = () => {
    setAddCardModalVisible(true);
    setSdkLoaded(false);
    setBindingStatus(null);
    setBindingError(null);

    // Initialize after modal opens
    setTimeout(() => {
      initializePayuni();
    }, 300);
  };

  // Handle modal close
  const handleAddCardModalClose = () => {
    setAddCardModalVisible(false);
    setAddCardLoading(false);
    setSdkLoaded(false);
    setBindingStatus(null);
    setBindingError(null);
    setCurrentSdkToken(null); // Clear SDK token

    // Cleanup PayUNi instance
    if (uniPaymentRef.current) {
      uniPaymentRef.current = null;
    }
  };

  // Handle card submission - calls PayUNi getTradeResult then backend bind_card API
  const handleSubmitCard = async () => {
    if (!uniPaymentRef.current) {
      message.error(t('operator.governance.payment_methods.sdk_not_initialized'));
      return;
    }

    if (!currentSdkToken) {
      message.error(t('operator.governance.payment_methods.sdk_not_initialized'));
      return;
    }

    try {
      setBindingStatus('processing');
      console.log('[PayUNi] Calling getTradeResult...');

      // Step 1: Call PayUNi SDK to complete card input validation
      const tradeResult = await uniPaymentRef.current.getTradeResult();
      console.log('[PayUNi] getTradeResult success:', tradeResult);

      // Step 2: Call backend API to complete card binding (幕後交易授權)
      console.log('[PayUNi] Calling backend bind_card API...');
      const bindResponse = await governanceService.bindCard(currentSdkToken, true);
      console.log('[PayUNi] bind_card response:', bindResponse);

      if (bindResponse.success) {
        // Check if 3D Secure is required
        if (bindResponse.data?.requires_3d && bindResponse.data?.redirect_url) {
          console.log('[PayUNi] 3D Secure required, redirecting...');
          message.info(t('operator.governance.payment_methods.redirecting_to_3d'));

          // Store current state for when user returns
          sessionStorage.setItem('payuni_binding_in_progress', 'true');

          // Redirect to 3D authentication page
          // After 3D completes, PayUNi will send webhook to our NotifyURL
          window.location.href = bindResponse.data.redirect_url;
          return;
        }

        setBindingStatus('success');
        message.success(t('operator.governance.payment_methods.add_card_success'));

        // Refresh payment methods list after a delay
        setTimeout(() => {
          fetchData();
          setAddCardModalVisible(false);
        }, 2000);
      } else {
        throw new Error(bindResponse.error || bindResponse.message || 'Card binding failed');
      }

    } catch (error) {
      console.error('[PayUNi] Card binding failed:', error);
      setBindingStatus('error');
      setBindingError(error?.message || error?.Message || t('operator.governance.payment_methods.card_binding_failed'));
    }
  };

  const handleDelete = async () => {
    if (!selectedMethod) return;

    try {
      setDeleting(true);
      const response = await governanceService.removePaymentMethod(selectedMethod.id);

      if (response.success) {
        message.success(t('operator.governance.payment_methods.delete_success'));
        setDeleteModalVisible(false);
        setSelectedMethod(null);
        fetchData();
      } else {
        message.error(response.message || t('common.error.delete_failed'));
      }
    } catch (error) {
      console.error('Failed to delete payment method:', error);
      message.error(t('common.error.delete_failed'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSetDefault = async (methodId) => {
    try {
      setSettingDefault(true);
      const response = await governanceService.setDefaultPaymentMethod(methodId);

      if (response.success) {
        message.success(t('operator.governance.payment_methods.set_default_success'));
        fetchData();
      } else {
        message.error(response.message || t('common.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to set default payment method:', error);
      message.error(t('common.error.update_failed'));
    } finally {
      setSettingDefault(false);
    }
  };

  const showDeleteConfirm = (method) => {
    setSelectedMethod(method);
    setDeleteModalVisible(true);
  };

  const columns = [
    {
      title: t('operator.governance.payment_methods.type'),
      dataIndex: 'method_type',
      key: 'method_type',
      render: (type) => (
        <Space>
          <CreditCardOutlined />
          {t(`operator.governance.payment_methods.types.${type}`)}
        </Space>
      )
    },
    {
      title: t('operator.governance.payment_methods.card_info'),
      key: 'card_info',
      render: (_, record) => (
        <Space>
          <Tag color={CARD_BRAND_COLORS[record.card_brand] || CARD_BRAND_COLORS.unknown}>
            {record.card_brand?.toUpperCase() || 'CARD'}
          </Tag>
          <Text>**** **** **** {record.card_last_four}</Text>
        </Space>
      )
    },
    {
      title: t('operator.governance.payment_methods.expiry'),
      key: 'expiry',
      render: (_, record) => {
        if (!record.card_exp_month || !record.card_exp_year) {
          return '-';
        }

        const expiry = `${String(record.card_exp_month).padStart(2, '0')}/${record.card_exp_year}`;

        if (record.card_expired) {
          return (
            <Tag color="red" icon={<ExclamationCircleOutlined />}>
              {expiry} ({t('operator.governance.payment_methods.expired')})
            </Tag>
          );
        }

        if (record.card_expiring_soon) {
          return (
            <Tag color="orange" icon={<ExclamationCircleOutlined />}>
              {expiry} ({t('operator.governance.payment_methods.expiring_soon')})
            </Tag>
          );
        }

        return expiry;
      }
    },
    {
      title: t('operator.governance.payment_methods.default'),
      dataIndex: 'is_default',
      key: 'is_default',
      align: 'center',
      render: (isDefault, record) => (
        isDefault ? (
          <Tag color="green" icon={<StarFilled />}>
            {t('operator.governance.payment_methods.default_label')}
          </Tag>
        ) : (
          <Tooltip title={t('operator.governance.payment_methods.set_as_default')}>
            <Button
              type="link"
              icon={<StarOutlined />}
              onClick={() => handleSetDefault(record.id)}
              loading={settingDefault}
            />
          </Tooltip>
        )
      )
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Tooltip title={t('common.button.delete')}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => showDeleteConfirm(record)}
          />
        </Tooltip>
      )
    }
  ];

  // Render add card modal content
  const renderAddCardContent = () => {
    if (bindingStatus === 'success') {
      return (
        <Result
          status="success"
          icon={<CheckCircleOutlined />}
          title={t('operator.governance.payment_methods.add_card_success')}
          subTitle={t('operator.governance.payment_methods.card_added_desc')}
        />
      );
    }

    if (bindingStatus === 'error') {
      return (
        <Result
          status="error"
          icon={<CloseCircleOutlined />}
          title={t('operator.governance.payment_methods.add_card_failed')}
          subTitle={bindingError}
          extra={[
            <Button key="retry" type="primary" onClick={initializePayuni}>
              {t('common.button.retry')}
            </Button>,
            <Button key="cancel" onClick={handleAddCardModalClose}>
              {t('common.button.cancel')}
            </Button>
          ]}
        />
      );
    }

    return (
      <>
        <Alert
          message={t('operator.governance.payment_methods.card_binding_notice')}
          description={t('operator.governance.payment_methods.card_binding_desc')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {addCardLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <Text style={{ marginLeft: 12 }}>{t('operator.governance.payment_methods.loading_payment_form')}</Text>
          </div>
        )}

        {/* PayUNi UNi Embed Container - SDK renders inputs into these divs */}
        <div
          ref={payuniContainerRef}
          id="payuni-container"
          style={{
            display: addCardLoading ? 'none' : 'block'
          }}
        >
          {/* Card Number */}
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {t('operator.governance.payment_methods.card_number')}
            </Text>
            <div
              id="payuni-card-no"
              style={{
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '8px 12px',
                minHeight: 40,
                backgroundColor: '#fff'
              }}
            />
          </div>

          {/* Card Expiry and CVC in a row */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {t('operator.governance.payment_methods.card_expiry')}
              </Text>
              <div
                id="payuni-card-exp"
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: '8px 12px',
                  minHeight: 40,
                  backgroundColor: '#fff'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {t('operator.governance.payment_methods.card_cvc')}
              </Text>
              <div
                id="payuni-card-cvc"
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: '8px 12px',
                  minHeight: 40,
                  backgroundColor: '#fff'
                }}
              />
            </div>
          </div>

          {/* Token Type Checkbox Area (for 約定信用卡) - initially hidden */}
          <div
            id="payuni-token-type-area"
            style={{ display: 'none', alignItems: 'center', marginBottom: 16 }}
          >
            <div id="payuni-token-type" style={{ display: 'flex', alignItems: 'center' }} />
            <label
              id="payuni-token-type-text"
              htmlFor="type-checkbox"
              style={{ marginLeft: 8, color: '#666', fontSize: 12 }}
            />
          </div>

          {/* Submit Button */}
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Button
              type="primary"
              size="large"
              icon={<CreditCardOutlined />}
              onClick={handleSubmitCard}
              loading={bindingStatus === 'processing'}
              style={{ minWidth: 200 }}
            >
              {t('operator.governance.payment_methods.bind_card')}
            </Button>
          </div>
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <ListPageHeader items={['governance', 'governance.payment_methods']} />

      {/* Credit Card Section */}
      <Card
        title={
          <Space>
            <CreditCardOutlined />
            {t('operator.governance.payment_methods.credit_cards')}
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCard}
          >
            {t('operator.governance.payment_methods.add_card')}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {paymentMethods.length > 0 ? (
          <Table
            dataSource={paymentMethods}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('operator.governance.payment_methods.no_cards')}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCard}
            >
              {t('operator.governance.payment_methods.add_first_card')}
            </Button>
          </Empty>
        )}
      </Card>

      {/* Bank Transfer Section (Taiwan Only) */}
      {bankTransferInfo?.available && !isEnterprise && (
        <Card
          title={
            <Space>
              <BankOutlined />
              {t('operator.governance.payment_methods.bank_transfer')}
            </Space>
          }
        >
          <Alert
            message={t('operator.governance.payment_methods.bank_transfer_info')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('operator.governance.payment_methods.bank_name')}>
              {bankTransferInfo.bank_name}
            </Descriptions.Item>
            <Descriptions.Item label={t('operator.governance.payment_methods.account_name')}>
              {bankTransferInfo.account_name}
            </Descriptions.Item>
            <Descriptions.Item label={t('operator.governance.payment_methods.account_number')}>
              <Text copyable strong style={{ fontSize: '16px' }}>
                {bankTransferInfo.account_number}
              </Text>
            </Descriptions.Item>
            {bankTransferInfo.notes && (
              <Descriptions.Item label={t('operator.governance.payment_methods.notes')}>
                <Text type="warning">{bankTransferInfo.notes}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* Add Card Modal */}
      <Modal
        title={
          <Space>
            <CreditCardOutlined />
            {t('operator.governance.payment_methods.add_card')}
          </Space>
        }
        open={addCardModalVisible}
        onCancel={handleAddCardModalClose}
        footer={bindingStatus ? null : [
          <Button key="cancel" onClick={handleAddCardModalClose}>
            {t('common.button.cancel')}
          </Button>
        ]}
        width={600}
        destroyOnHidden
      >
        {renderAddCardContent()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title={t('operator.governance.payment_methods.delete_confirm_title')}
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedMethod(null);
        }}
        confirmLoading={deleting}
        okText={t('common.button.delete')}
        okButtonProps={{ danger: true }}
        cancelText={t('common.button.cancel')}
      >
        {selectedMethod && (
          <p>
            {t('operator.governance.payment_methods.delete_confirm_desc', {
              card: `**** ${selectedMethod.card_last_four}`
            })}
          </p>
        )}
        <Alert
          message={t('operator.governance.payment_methods.delete_warning')}
          type="warning"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default PaymentMethods;
